/**
 * A whole profile, as a panel over the app.
 *
 * Same idea as settings: looking at somebody should not cost you your place.
 * The page version still exists for a direct link, but everywhere inside the
 * app a profile opens here and closes back to whatever you were doing.
 *
 * Profiles stack. Opening a mutual friend from inside a profile lays theirs on
 * top and Escape peels it back off, so you can follow a chain of people and
 * still find your way home. Before, a mutual friend opened the small user card
 * instead - and that card sits below this panel in the layer order, so it
 * appeared underneath and looked like nothing had happened.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBackdropClose } from '@/lib/backdrop';
import { useLayer } from '@/lib/layers';
import { createPortal } from 'react-dom';
import { ProfileView } from '@/views/ProfileView';
import { Icon } from '@/icons/Icon';

/*
 * Opening one, from anywhere.
 *
 * The panel used to be mounted in the shell against a piece of state that
 * nothing ever set - setProfileOpen was called with null and never with a
 * name - so it could not be reached at all, and clicking somebody's name only
 * ever took you to the full page. Same one-host pattern the crew panel uses:
 * anywhere can ask, one place draws it.
 */
let openName: string | null = null;
const watchers = new Set<() => void>();

export function openProfile(username: string): void {
  openName = username;
  watchers.forEach((fn) => fn());
}

/** Shuts the profile panel, from anywhere. */
export function closeProfile(): void {
  if (!openName) return;
  openName = null;
  watchers.forEach((fn) => fn());
}

export function ProfilePanelHost() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    watchers.add(fn);
    return () => { watchers.delete(fn); };
  }, []);

  if (!openName) return null;
  return (
    <ProfilePanel
      username={openName}
      onClose={() => { openName = null; watchers.forEach((fn) => fn()); }}
    />
  );
}

export function ProfilePanel({ username, onClose }: { username: string; onClose: () => void }) {
  const [stack, setStack] = useState<string[]>([username]);

  /*
   * Closing is a movement, not a disappearance.
   *
   * The panel used to be unmounted the moment you pressed the cross, which
   * on a screen-filling thing reads as a glitch. It now plays the entrance
   * backwards and unmounts when that finishes - and the flag guards against
   * a second press starting a second exit.
   */
  const [closing, setClosing] = useState(false);
  const leave = useCallback(() => {
    setClosing((was) => {
      if (was) return was;
      setTimeout(onClose, EXIT_MS);
      return true;
    });
  }, [onClose]);

  const backdrop = useBackdropClose(leave);

  /* On the shared stack: its scrim fills the window, so a press on the dimmed
     area closes anything drawn over the profile as well as the profile. */
  const shell = useRef<HTMLDivElement>(null);
  useLayer(!closing, { node: () => shell.current, close: leave });

  // Opening a different person from outside resets the chain.
  useEffect(() => { setStack([username]); }, [username]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      // Escape peels one profile off; the last one closes the panel.
      setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
      if (stack.length <= 1) leave();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [leave, stack.length]);

  function back() {
    if (stack.length > 1) setStack((s) => s.slice(0, -1));
    else leave();
  }

  return createPortal(
    <div
      ref={shell}
      data-keeps-card
      className={
        (closing ? 'veil-out' : 'soft-in') +
        ' fixed inset-0 z-[300] flex bg-[rgba(3,5,8,.66)] backdrop-blur-[5px]'
      }
      {...backdrop}
    >
      {stack.map((name, i) => (
        <div
          key={name + i}
          // Each one further up the stack sits a layer higher and a few pixels
          // in, so the one underneath still shows at the edge and the depth is
          // visible rather than implied.
          style={{ zIndex: i, transform: i ? `translate(${i * 10}px, ${i * 10}px)` : undefined }}
          className={cnPanel(i === stack.length - 1, closing)}
        >
          <div className="absolute top-3 right-3 z-[2] flex items-center gap-1.5">
            {i > 0 ? (
              <button
                type="button"
                title="Back"
                onClick={back}
                className="grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-[rgba(0,0,0,.42)] text-white/85 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.62)] hover:text-white"
              >
                <Icon name="chevronL" size={15} stroke={2.2} />
              </button>
            ) : null}
            <button
              type="button"
              title="Close"
              onClick={leave}
              className="grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-[rgba(0,0,0,.42)] text-white/85 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.62)] hover:text-white"
            >
              <Icon name="close" size={15} stroke={2.2} />
            </button>
          </div>

          {/* The windowed shape. The page keeps its long single column,
              which is right for something you navigated to on purpose. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <ProfileView
              compact
              username={name}
              onNavigate={leave}
              onOpenUser={(next) => setStack((s) => (s.includes(next) ? s : [...s, next]))}
            />
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
}

/** How long the exit takes. Matches .profile-out in the stylesheet. */
const EXIT_MS = 150;

/** The panel shell. Only the top of the stack takes pointer events. */
function cnPanel(top: boolean, closing: boolean): string {
  return [
    closing ? 'profile-out' : 'profile-in',
    /*
       A window, not the screen.

       86vw by 88vh is the whole application with a hole cut in it - opened
       to check one thing about somebody, it covered everything you were
       doing. A profile is a card: wide enough for a column of facts beside
       a column of content, tall enough to read without being a page.
    */
    'settings-in absolute inset-0 m-auto flex h-[min(620px,86vh)] w-[min(940px,94vw)] flex-col',
    'overflow-hidden rounded-[var(--radius-xl)] border border-line bg-surface-0 shadow-[var(--shadow-3)]',
    top ? '' : 'pointer-events-none'
  ].join(' ');
}
